/**
 * Auto-placement inside MB contour (local km).
 * Producers: regular / checkerboard with producer–producer spacing.
 * Injectors: derived from that producer lattice + prod:inj ratio (not spaced independently).
 */
import type { MbPoint } from '@/types/materialBalance';

/** regular = orthogonal lattice; checkerboard = rows offset by half producer spacing */
export type ProdGridPattern = 'regular' | 'checkerboard';

export type AutoPlaceParams = {
  pattern: ProdGridPattern;
  /** Producer–producer spacing in meters (injectors are fitted to this lattice). */
  spacingM: number;
  /** Ratio parts: producers : injectors (e.g. 1:3 or 3:1). inj=0 → producers only */
  production: number;
  injection: number;
  /** Earliest year producers / injectors can come online */
  startYear: number;
  /** Max new producers per calendar year */
  maxProdPerYear: number;
  /** Max new injectors per calendar year */
  maxInjPerYear: number;
  /** Forecast end year (well t1) */
  year1: number;
  qStartTd: number;
};

export type AutoPlaceWell = {
  well_name: string;
  kind: 'prod' | 'inj';
  x: number;
  y: number;
  t0: number;
  t1: number;
  q_start_td: number | null;
};

export type AutoPlaceApplyMode = 'replace' | 'delta';

export type AutoPlaceResult = {
  wells: AutoPlaceWell[];
  injField: Record<string, number>;
  /** replace (default): rewrite all layer wells; delta: create/update/delete only changes */
  applyMode?: AutoPlaceApplyMode;
  /** manual: hand-placed wells — the saved grid settings (auto_place) are left untouched */
  source?: 'manual';
  meta: {
    areaKm2: number;
    nProd: number;
    nInj: number;
    spacingKm: number;
    spacingM: number;
    pattern: ProdGridPattern;
    production: number;
    injection: number;
    startYear: number;
    maxProdPerYear: number;
    maxInjPerYear: number;
    lastOnlineYear: number;
  };
};

const _nowY = new Date().getFullYear();

export const AUTO_PLACE_DEFAULTS: AutoPlaceParams = {
  pattern: 'regular',
  spacingM: 200,
  production: 1,
  injection: 1,
  startYear: _nowY,
  maxProdPerYear: 2,
  maxInjPerYear: 2,
  year1: _nowY + 16,
  /** Fallback when layer has no ML/manual q_liq_start_td yet */
  qStartTd: 100,
};

export function pointInPoly(x: number, y: number, poly: MbPoint[]): boolean {
  let inside = false;
  const n = poly.length;
  for (let i = 0; i < n; i += 1) {
    const j = (i - 1 + n) % n;
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-15) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function distPointSeg(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const t = Math.max(
    0,
    Math.min(1, ((px - ax) * abx + (py - ay) * aby) / (abx * abx + aby * aby || 1e-15)),
  );
  return Math.hypot(px - (ax + t * abx), py - (ay + t * aby));
}

function distToBoundary(x: number, y: number, poly: MbPoint[]): number {
  let min = Infinity;
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    min = Math.min(min, distPointSeg(x, y, a.x, a.y, b.x, b.y));
  }
  return min;
}

export function contourAreaKm2(poly: MbPoint[]): number {
  if (poly.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < poly.length; i += 1) {
    const j = (i + 1) % poly.length;
    a += poly[i].x * poly[j].y - poly[j].x * poly[i].y;
  }
  return Math.abs(a) * 0.5;
}

/** Above this the O(n²) injector / scheduling passes freeze the browser tab. */
export const MAX_AUTO_PLACE_PRODUCERS = 1500;
const MAX_AUTO_PLACE_LATTICE_NODES = 2_000_000;

/** Approximate producer count and lattice size for a spacing, before running autoPlaceWells. */
export function estimateAutoPlaceGrid(
  poly: MbPoint[],
  spacingM: number,
): { producers: number; tooLarge: boolean; minSpacingM: number } {
  if (poly.length < 3 || !(spacingM > 0)) {
    return { producers: 0, tooLarge: false, minSpacingM: 0 };
  }
  const s = spacingM / 1000;
  const area = contourAreaKm2(poly);
  const b = bounds(poly);
  const w = b.maxX - b.minX;
  const h = b.maxY - b.minY;
  const producers = Math.round(area / (s * s));
  const nodes = (2 * (Math.ceil(w / s) + 3) + 2) * (2 * (Math.ceil(h / s) + 3) + 1);
  const minByProducers = Math.sqrt(area / MAX_AUTO_PLACE_PRODUCERS);
  const minByNodes = Math.sqrt((4 * w * h) / MAX_AUTO_PLACE_LATTICE_NODES);
  return {
    producers,
    tooLarge: producers > MAX_AUTO_PLACE_PRODUCERS || nodes > MAX_AUTO_PLACE_LATTICE_NODES,
    minSpacingM: Math.ceil(Math.max(minByProducers, minByNodes) * 1000),
  };
}

function bounds(poly: MbPoint[]) {
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

function centroid(poly: MbPoint[]): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (const p of poly) {
    x += p.x;
    y += p.y;
  }
  const n = Math.max(poly.length, 1);
  return { x: x / n, y: y / n };
}

function acceptPoint(x: number, y: number, poly: MbPoint[], bufferKm: number): boolean {
  if (!pointInPoly(x, y, poly)) return false;
  if (bufferKm > 0 && distToBoundary(x, y, poly) < bufferKm) return false;
  return true;
}

function dist2(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
}

/** Merge points closer than mergeKm into averages. */
function mergeNearby(
  pts: { x: number; y: number }[],
  mergeKm: number,
): { x: number; y: number }[] {
  const merge2 = mergeKm * mergeKm;
  const used = new Array(pts.length).fill(false);
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < pts.length; i += 1) {
    if (used[i]) continue;
    let sx = pts[i].x;
    let sy = pts[i].y;
    let n = 1;
    used[i] = true;
    for (let j = i + 1; j < pts.length; j += 1) {
      if (used[j]) continue;
      if (dist2(pts[i], pts[j]) <= merge2) {
        used[j] = true;
        sx += pts[j].x;
        sy += pts[j].y;
        n += 1;
      }
    }
    out.push({ x: +(sx / n).toFixed(4), y: +(sy / n).toFixed(4) });
  }
  return out;
}

/**
 * Sample lattice with origin (ox, oy). Checkerboard offsets odd rows by +s/2
 * and widens i-range so the shift is symmetric (not biased to the right).
 */
function sampleLattice(
  poly: MbPoint[],
  ox: number,
  oy: number,
  s: number,
  pattern: ProdGridPattern,
  edgeBuf: number,
): { x: number; y: number }[] {
  const b = bounds(poly);
  const nX = Math.ceil((b.maxX - b.minX) / s) + 3;
  const nY = Math.ceil((b.maxY - b.minY) / s) + 3;
  const out: { x: number; y: number }[] = [];
  const seen = new Set<string>();

  for (let j = -nY; j <= nY; j += 1) {
    const odd = pattern === 'checkerboard' && j % 2 !== 0;
    const i0 = odd ? -nX - 1 : -nX;
    const i1 = odd ? nX : nX;
    for (let i = i0; i <= i1; i += 1) {
      const x = odd ? ox + (i + 0.5) * s : ox + i * s;
      const y = oy + j * s;
      if (!acceptPoint(x, y, poly, edgeBuf)) continue;
      const key = `${x.toFixed(4)},${y.toFixed(4)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ x: +x.toFixed(4), y: +y.toFixed(4) });
    }
  }
  return out;
}

/**
 * Place producers evenly: lattice origin is iteratively recentered so the
 * cloud of accepted wells stays balanced around the contour centroid (P1 ≈ center).
 */
export function placeProducerGrid(
  poly: MbPoint[],
  spacingKm: number,
  pattern: ProdGridPattern,
  bufferKm: number,
): { x: number; y: number }[] {
  const s = Math.max(spacingKm, 1e-6);
  const b = bounds(poly);
  const charSize = Math.max(b.maxX - b.minX, b.maxY - b.minY, 0.05);
  // Light edge buffer — heavy buffer punched holes on one side of irregular contours
  const edgeBuf = Math.min(bufferKm * 0.35, charSize * 0.06);

  let cx = centroid(poly).x;
  let cy = centroid(poly).y;

  if (!pointInPoly(cx, cy, poly)) {
    let best = { x: cx, y: cy, d: Infinity };
    const step = Math.min(s, Math.max(charSize / 20, 0.01));
    for (let y = b.minY; y <= b.maxY; y += step) {
      for (let x = b.minX; x <= b.maxX; x += step) {
        if (!pointInPoly(x, y, poly)) continue;
        const d = (x - cx) ** 2 + (y - cy) ** 2;
        if (d < best.d) best = { x, y, d };
      }
    }
    cx = best.x;
    cy = best.y;
  }

  // Iteratively shift lattice origin so mean(wells) ≈ contour centroid.
  // Keep a node near the center by snapping origin — never insert an extra
  // point (that densified the grid to ~s/2 and made injectors land on prods).
  let ox = cx;
  let oy = cy;
  let pts: { x: number; y: number }[] = [];
  for (let iter = 0; iter < 6; iter += 1) {
    pts = sampleLattice(poly, ox, oy, s, pattern, edgeBuf);
    if (pts.length === 0) break;
    const mx = pts.reduce((a, p) => a + p.x, 0) / pts.length;
    const my = pts.reduce((a, p) => a + p.y, 0) / pts.length;
    const dx = cx - mx;
    const dy = cy - my;
    if (Math.hypot(dx, dy) < s * 0.02) break;
    ox += dx;
    oy += dy;
  }

  pts.sort((a, b) => dist2(a, { x: cx, y: cy }) - dist2(b, { x: cx, y: cy }));
  return pts;
}

/** Reject injector candidates that sit on / too close to a producer. */
function clearOfProducers(
  pts: { x: number; y: number }[],
  producers: { x: number; y: number }[],
  minDistKm: number,
): { x: number; y: number }[] {
  const min2 = minDistKm * minDistKm;
  return pts.filter((pt) => !producers.some((p) => dist2(pt, p) < min2));
}

/**
 * Injectors for ratio production:injection.
 * - 1:1 — dual lattice (midpoints between neighboring producers), never on prods
 * - inj > prod (e.g. 1:3): ring around each producer, merge shared candidates
 * - prod > inj (e.g. 3:1): cluster producers, one injector near cluster centroid
 */
export function placeInjectorsForRatio(
  producers: { x: number; y: number }[],
  spacingKm: number,
  production: number,
  injection: number,
  contour: MbPoint[],
): { x: number; y: number }[] {
  const prodPart = Math.max(1, Math.floor(production));
  const injPart = Math.max(0, Math.floor(injection));
  if (injPart <= 0 || producers.length === 0) return [];

  // `spacingKm` is producer–producer pitch; injectors sit on the dual / around prods.
  const s = Math.max(spacingKm, 1e-6);
  const r = s * 0.5;
  const mergeKm = s * 0.28;
  // Stay clear of producers (and of densified accidental near-neighbors)
  const prodClear = s * 0.22;

  const finalize = (candidates: { x: number; y: number }[]) =>
    clearOfProducers(
      mergeNearby(candidates, mergeKm).filter((pt) => pointInPoly(pt.x, pt.y, contour)),
      producers,
      prodClear,
    );

  if (injPart >= prodPart) {
    const perProd = Math.max(1, Math.round(injPart / prodPart));

    // 1:1 — midpoints of neighboring producer pairs (true dual of the prod lattice)
    if (perProd === 1) {
      const candidates: { x: number; y: number }[] = [];
      for (let i = 0; i < producers.length; i += 1) {
        for (let j = i + 1; j < producers.length; j += 1) {
          const d = Math.hypot(
            producers[i].x - producers[j].x,
            producers[i].y - producers[j].y,
          );
          // Neighbors on the producer lattice (~1 step, allow slight irregularity)
          if (d < s * 0.65 || d > s * 1.4) continue;
          const x = (producers[i].x + producers[j].x) / 2;
          const y = (producers[i].y + producers[j].y) / 2;
          if (pointInPoly(x, y, contour)) {
            candidates.push({ x: +x.toFixed(4), y: +y.toFixed(4) });
          }
        }
      }
      // Thin / single-well contours: fall back to cardinal offsets
      if (candidates.length === 0) {
        for (const p of producers) {
          for (const [dx, dy] of [
            [r, 0],
            [-r, 0],
            [0, r],
            [0, -r],
          ] as const) {
            const x = p.x + dx;
            const y = p.y + dy;
            if (pointInPoly(x, y, contour)) {
              candidates.push({ x: +x.toFixed(4), y: +y.toFixed(4) });
            }
          }
        }
      }
      return finalize(candidates);
    }

    // More injectors per producer — ring around each producer
    const candidates: { x: number; y: number }[] = [];
    // Rotate 45° for 4-spot so injectors land in cell centers of a square lattice
    const angle0 = perProd === 4 ? Math.PI / 4 : 0;
    for (const p of producers) {
      for (let k = 0; k < perProd; k += 1) {
        const ang = angle0 + (2 * Math.PI * k) / perProd;
        const x = p.x + r * Math.cos(ang);
        const y = p.y + r * Math.sin(ang);
        if (pointInPoly(x, y, contour)) {
          candidates.push({ x: +x.toFixed(4), y: +y.toFixed(4) });
        }
      }
    }
    return finalize(candidates);
  }

  // More producers per injector — cluster producers, one injector at centroid
  const groupSize = prodPart;
  const unused = producers.map((p, i) => ({ ...p, i }));
  const taken = new Set<number>();
  const injectors: { x: number; y: number }[] = [];

  while (taken.size < producers.length) {
    const seed = unused.find((p) => !taken.has(p.i));
    if (!seed) break;
    const remaining = unused
      .filter((p) => !taken.has(p.i))
      .sort((a, b) => dist2(a, seed) - dist2(b, seed));
    const group = remaining.slice(0, groupSize);
    if (group.length === 0) break;
    for (const g of group) taken.add(g.i);

    // Prefer midpoint of the two closest wells in the group (avoids landing on a prod
    // when the group centroid coincides with the middle well of a line).
    let ix: number;
    let iy: number;
    if (group.length >= 2) {
      let best = { i: 0, j: 1, d: Infinity };
      for (let i = 0; i < group.length; i += 1) {
        for (let j = i + 1; j < group.length; j += 1) {
          const d = dist2(group[i], group[j]);
          if (d < best.d) best = { i, j, d };
        }
      }
      ix = (group[best.i].x + group[best.j].x) / 2;
      iy = (group[best.i].y + group[best.j].y) / 2;
    } else {
      // Single leftover producer — offset to the dual, not onto the well
      ix = seed.x + r;
      iy = seed.y;
      if (!pointInPoly(ix, iy, contour)) {
        ix = seed.x;
        iy = seed.y + r;
      }
      if (!pointInPoly(ix, iy, contour)) {
        ix = seed.x - r;
        iy = seed.y;
      }
      if (!pointInPoly(ix, iy, contour)) continue;
    }

    if (pointInPoly(ix, iy, contour)) {
      injectors.push({ x: +ix.toFixed(4), y: +iy.toFixed(4) });
    }
  }

  return clearOfProducers(injectors, producers, prodClear);
}

export function buildDefaultInjField(
  year0: number,
  year1: number,
  nProd: number,
  qStartTd: number,
  hasInj: boolean,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!hasInj) {
    for (let y = year0; y <= year1; y += 1) out[String(y)] = 0;
    return out;
  }
  const liqPeak = Math.max(nProd * Math.max(qStartTd, 1) * 0.365, 50);
  const peak = liqPeak * 0.9;
  for (let y = year0; y <= year1; y += 1) {
    const t = (y - year0) / Math.max(year1 - year0, 1);
    out[String(y)] = Math.round(peak * (0.55 + 0.45 * t) * 10) / 10;
  }
  return out;
}

type SchedSeed = {
  kind: 'prod' | 'inj';
  x: number;
  y: number;
};

/**
 * Order wells center → edge, then clockwise (atan2 from north, CW).
 */
export function orderCenterOutClockwise<T extends { x: number; y: number }>(
  pts: T[],
  center: { x: number; y: number },
): T[] {
  return [...pts].sort((a, b) => {
    const da = dist2(a, center);
    const db = dist2(b, center);
    if (Math.abs(da - db) > 1e-12) return da - db;
    // atan2(x, y): 0 = north, increasing clockwise
    const aa = Math.atan2(a.x - center.x, a.y - center.y);
    const ab = Math.atan2(b.x - center.x, b.y - center.y);
    return aa - ab;
  });
}

/**
 * Assign online years (t0) from capacity limits, center-out order, keeping
 * cumulative prod:inj as close as possible to the target ratio parts.
 */
export function scheduleWellOnlineYears(
  seeds: SchedSeed[],
  opts: {
    center: { x: number; y: number };
    startYear: number;
    maxProdPerYear: number;
    maxInjPerYear: number;
    production: number;
    injection: number;
  },
): { kind: 'prod' | 'inj'; x: number; y: number; t0: number }[] {
  const startY = Math.floor(opts.startYear);
  const maxP = Math.max(0, Math.floor(opts.maxProdPerYear));
  const maxI = Math.max(0, Math.floor(opts.maxInjPerYear));
  const prodPart = Math.max(1, Math.floor(opts.production));
  const injPart = Math.max(0, Math.floor(opts.injection));

  const ordered = orderCenterOutClockwise(seeds, opts.center);
  const pendingProd = ordered.filter((w) => w.kind === 'prod');
  const pendingInj = ordered.filter((w) => w.kind === 'inj');
  let iP = 0;
  let iI = 0;

  const out: { kind: 'prod' | 'inj'; x: number; y: number; t0: number }[] = [];
  let nProd = 0;
  let nInj = 0;

  const ratioScoreIfAdd = (kind: 'prod' | 'inj') => {
    const np = nProd + (kind === 'prod' ? 1 : 0);
    const ni = nInj + (kind === 'inj' ? 1 : 0);
    if (injPart <= 0) return kind === 'prod' ? 0 : 1e9;
    // Distance from ideal np/prodPart == ni/injPart
    return Math.abs(np / prodPart - ni / injPart);
  };

  // Safety: never loop forever if capacity is zero for remaining kinds
  const hardCapYears = Math.max(pendingProd.length + pendingInj.length, 1) + 64;
  for (let step = 0; step < hardCapYears; step += 1) {
    const leftP = pendingProd.length - iP;
    const leftI = pendingInj.length - iI;
    if (leftP + leftI === 0) break;

    const year = startY + step;
    let slotP = maxP;
    let slotI = maxI;

    if (maxP <= 0 && leftP > 0 && leftI === 0) break;
    if (maxI <= 0 && leftI > 0 && leftP === 0) break;

    let placed = 0;
    while (
      slotP + slotI > 0 &&
      (iP < pendingProd.length || iI < pendingInj.length)
    ) {
      const canP = slotP > 0 && iP < pendingProd.length;
      const canI = slotI > 0 && iI < pendingInj.length;
      if (!canP && !canI) break;

      let pick: 'prod' | 'inj';
      if (canP && canI) {
        pick = ratioScoreIfAdd('prod') <= ratioScoreIfAdd('inj') ? 'prod' : 'inj';
      } else if (canP) {
        pick = 'prod';
      } else {
        pick = 'inj';
      }

      if (pick === 'prod') {
        const w = pendingProd[iP];
        iP += 1;
        slotP -= 1;
        nProd += 1;
        out.push({ kind: 'prod', x: w.x, y: w.y, t0: year });
      } else {
        const w = pendingInj[iI];
        iI += 1;
        slotI -= 1;
        nInj += 1;
        out.push({ kind: 'inj', x: w.x, y: w.y, t0: year });
      }
      placed += 1;
    }

    if (placed === 0) {
      if (!((leftP > 0 && maxP > 0) || (leftI > 0 && maxI > 0))) break;
    }
  }

  // Fallback: anything still pending (zero capacity) gets last scheduled year or start
  const fallbackY = out.length ? Math.max(...out.map((w) => w.t0)) : startY;
  while (iP < pendingProd.length) {
    const w = pendingProd[iP];
    iP += 1;
    out.push({ kind: 'prod', x: w.x, y: w.y, t0: fallbackY });
  }
  while (iI < pendingInj.length) {
    const w = pendingInj[iI];
    iI += 1;
    out.push({ kind: 'inj', x: w.x, y: w.y, t0: fallbackY });
  }

  return out;
}

export function autoPlaceWells(
  contour: MbPoint[],
  params: Partial<AutoPlaceParams> = {},
): AutoPlaceResult {
  const p: AutoPlaceParams = { ...AUTO_PLACE_DEFAULTS, ...params };
  const area = contourAreaKm2(contour);
  const spacingM =
    Number.isFinite(p.spacingM) && p.spacingM > 0 ? p.spacingM : AUTO_PLACE_DEFAULTS.spacingM;
  const spacingKm = spacingM / 1000;
  const bufferKm = spacingKm * 0.35;
  const production = Math.max(1, Math.floor(p.production || 1));
  const injection = Math.max(0, Math.floor(p.injection || 0));

  const startYear = Math.floor(
    Number.isFinite(p.startYear) ? p.startYear : AUTO_PLACE_DEFAULTS.startYear,
  );
  const maxProdPerYear = Math.max(
    0,
    Math.floor(
      Number.isFinite(p.maxProdPerYear) ? p.maxProdPerYear : AUTO_PLACE_DEFAULTS.maxProdPerYear,
    ),
  );
  const maxInjPerYear = Math.max(
    0,
    Math.floor(
      Number.isFinite(p.maxInjPerYear) ? p.maxInjPerYear : AUTO_PLACE_DEFAULTS.maxInjPerYear,
    ),
  );
  const year1 = Math.max(
    startYear,
    Math.floor(Number.isFinite(p.year1) ? p.year1 : AUTO_PLACE_DEFAULTS.year1),
  );

  const prodPts = placeProducerGrid(contour, spacingKm, p.pattern, bufferKm);
  const injPts = placeInjectorsForRatio(
    prodPts,
    spacingKm,
    production,
    injection,
    contour,
  );

  const c = centroid(contour);
  const center = pointInPoly(c.x, c.y, contour)
    ? c
    : prodPts[0]
      ? { x: prodPts[0].x, y: prodPts[0].y }
      : c;

  const seeds: SchedSeed[] = [
    ...prodPts.map((pt) => ({ kind: 'prod' as const, x: pt.x, y: pt.y })),
    ...injPts.map((pt) => ({ kind: 'inj' as const, x: pt.x, y: pt.y })),
  ];

  const scheduled = scheduleWellOnlineYears(seeds, {
    center,
    startYear,
    maxProdPerYear: Math.max(1, maxProdPerYear),
    maxInjPerYear: injection > 0 ? Math.max(1, maxInjPerYear) : 0,
    production,
    injection,
  });

  // Stable names by kind order of online (center-out schedule order within kind)
  let pIdx = 0;
  let iIdx = 0;
  const wells: AutoPlaceWell[] = scheduled.map((w) => {
    if (w.kind === 'prod') {
      pIdx += 1;
      return {
        well_name: `P${pIdx}`,
        kind: 'prod' as const,
        x: w.x,
        y: w.y,
        t0: w.t0,
        t1: Math.max(year1, w.t0),
        q_start_td: p.qStartTd,
      };
    }
    iIdx += 1;
    return {
      well_name: `I${iIdx}`,
      kind: 'inj' as const,
      x: w.x,
      y: w.y,
      t0: w.t0,
      t1: Math.max(year1, w.t0),
      q_start_td: null,
    };
  });

  const lastOnlineYear = wells.length
    ? Math.max(...wells.map((w) => w.t0))
    : startYear;
  const injFieldStart = wells.length
    ? Math.min(startYear, ...wells.map((w) => w.t0))
    : startYear;

  return {
    wells,
    injField: buildDefaultInjField(
      injFieldStart,
      Math.max(year1, lastOnlineYear),
      prodPts.length,
      p.qStartTd,
      injPts.length > 0,
    ),
    meta: {
      areaKm2: Math.round(area * 1000) / 1000,
      nProd: prodPts.length,
      nInj: injPts.length,
      spacingKm: Math.round(spacingKm * 1000) / 1000,
      spacingM: Math.round(spacingM * 1000) / 1000,
      pattern: p.pattern,
      production,
      injection,
      startYear,
      maxProdPerYear,
      maxInjPerYear,
      lastOnlineYear,
    },
  };
}

function normalizeAiWell(
  w: {
    well_name: string;
    kind: 'prod' | 'inj';
    x: number;
    y: number;
    t0: number;
    t1: number;
    q_start_td?: number | null;
  },
  year1: number,
  qStartTd: number,
): AutoPlaceWell | null {
  if (!Number.isFinite(w.x) || !Number.isFinite(w.y)) return null;
  const kind = w.kind === 'inj' ? 'inj' : 'prod';
  const t0 = Math.floor(w.t0);
  const t1 = Math.max(t0, Math.floor(w.t1 || year1));
  return {
    well_name: String(w.well_name || '').trim() || (kind === 'prod' ? 'P?' : 'I?'),
    kind,
    x: Number(w.x.toFixed(4)),
    y: Number(w.y.toFixed(4)),
    t0,
    t1: Math.max(year1, t1),
    q_start_td:
      kind === 'prod'
        ? w.q_start_td != null && w.q_start_td > 0
          ? w.q_start_td
          : qStartTd
        : null,
  };
}

/**
 * Merge AI delta into existing wells (by well_name).
 * replace → use AI wells only; delta → keep untouched existing, upsert AI, drop removals.
 */
export function mergeAiWellPlan(opts: {
  applyMode: AutoPlaceApplyMode;
  existing: AutoPlaceWell[];
  aiWells: AutoPlaceWell[];
  removeWellNames?: string[];
}): AutoPlaceWell[] {
  const { applyMode, existing, aiWells } = opts;
  if (applyMode === 'replace') return aiWells;

  const remove = new Set(
    (opts.removeWellNames || []).map((n) => n.trim().toLowerCase()).filter(Boolean),
  );
  const byName = new Map<string, AutoPlaceWell>();
  for (const w of existing) {
    const key = w.well_name.trim().toLowerCase();
    if (!key || remove.has(key)) continue;
    byName.set(key, w);
  }
  for (const w of aiWells) {
    const key = w.well_name.trim().toLowerCase();
    if (!key) continue;
    byName.set(key, w);
  }
  return [...byName.values()];
}

/**
 * Turn an AI / external well list into AutoPlaceResult (inj_field + meta)
 * so Apply / map preview share the same path as grid auto-place.
 */
export function finalizeAutoPlaceFromAiWells(
  wellsIn: Array<{
    well_name: string;
    kind: 'prod' | 'inj';
    x: number;
    y: number;
    t0: number;
    t1: number;
    q_start_td?: number | null;
  }>,
  contour: MbPoint[],
  opts: {
    year1: number;
    qStartTd: number;
    spacingM?: number;
    pattern?: ProdGridPattern;
    applyMode?: AutoPlaceApplyMode;
    existingWells?: AutoPlaceWell[];
    removeWellNames?: string[];
  },
): AutoPlaceResult {
  const year1 = Math.floor(opts.year1);
  const qStartTd =
    Number.isFinite(opts.qStartTd) && opts.qStartTd > 0
      ? opts.qStartTd
      : AUTO_PLACE_DEFAULTS.qStartTd;
  const applyMode: AutoPlaceApplyMode =
    opts.applyMode === 'delta' ? 'delta' : 'replace';

  const aiWells: AutoPlaceWell[] = [];
  for (const raw of wellsIn) {
    const w = normalizeAiWell(raw, year1, qStartTd);
    if (!w) continue;
    // Soft accept: keep even if slightly outside (backend already pulls inside).
    aiWells.push(w);
  }

  const wells = mergeAiWellPlan({
    applyMode,
    existing: opts.existingWells || [],
    aiWells,
    removeWellNames: opts.removeWellNames,
  });

  const nProd = wells.filter((w) => w.kind === 'prod').length;
  const nInj = wells.filter((w) => w.kind === 'inj').length;
  const startYear = wells.length
    ? Math.min(...wells.map((w) => w.t0))
    : AUTO_PLACE_DEFAULTS.startYear;
  const lastOnlineYear = wells.length
    ? Math.max(...wells.map((w) => w.t0))
    : startYear;
  const spacingM =
    opts.spacingM && opts.spacingM > 0
      ? opts.spacingM
      : estimateSpacingM(wells.filter((w) => w.kind === 'prod'));
  const spacingKm = spacingM / 1000;

  return {
    wells,
    applyMode,
    injField: buildDefaultInjField(
      startYear,
      Math.max(year1, lastOnlineYear),
      nProd,
      qStartTd,
      nInj > 0,
    ),
    meta: {
      areaKm2: Math.round(contourAreaKm2(contour) * 1000) / 1000,
      nProd,
      nInj,
      spacingKm: Math.round(spacingKm * 1000) / 1000,
      spacingM: Math.round(spacingM * 1000) / 1000,
      pattern: opts.pattern === 'checkerboard' ? 'checkerboard' : 'regular',
      production: 1,
      injection: nInj > 0 ? 1 : 0,
      startYear,
      maxProdPerYear: 1,
      maxInjPerYear: 1,
      lastOnlineYear,
    },
  };
}

/** Median nearest-neighbour distance among producers (m); fallback 200. */
function estimateSpacingM(prods: Array<{ x: number; y: number }>): number {
  if (prods.length < 2) return AUTO_PLACE_DEFAULTS.spacingM;
  const dists: number[] = [];
  for (let i = 0; i < prods.length; i += 1) {
    let best = Infinity;
    for (let j = 0; j < prods.length; j += 1) {
      if (i === j) continue;
      const d = Math.hypot(prods[i].x - prods[j].x, prods[i].y - prods[j].y);
      if (d > 1e-9 && d < best) best = d;
    }
    if (Number.isFinite(best) && best < Infinity) dists.push(best);
  }
  if (!dists.length) return AUTO_PLACE_DEFAULTS.spacingM;
  dists.sort((a, b) => a - b);
  const mid = dists[Math.floor(dists.length / 2)];
  return Math.max(50, Math.round(mid * 1000)); // km → m
}
